/**
 * Sessão nova do host limpa o que é do guest.
 *
 * O portal que embute derruba a sessão SSO no Keycloak quando desloga, mas não
 * alcança o `sessionStorage` desta SPA: o Keycloak não tem como, e o
 * `portal-gerador` é um client público sem endpoint de back-channel logout.
 *
 * Sem isto, o token daqui sobrevive ao logout de lá — e como `isAuthenticated()`
 * decide comparando `Date.now()` com o `exp` guardado, sem perguntar nada ao
 * servidor, quem logasse em seguida na mesma aba herdaria a sessão de quem
 * saiu. Dura até o access token expirar, uns cinco minutos.
 *
 * O host manda a identidade da sessão dele em `?hs=`. Se mudou, este módulo
 * apaga token e cache antes do React montar. Mora à parte do `embed/host.ts`
 * porque o `config/auth.ts` importa aquele módulo, e importar de volta fecharia
 * ciclo.
 */
import { clearSilentAuthAttempt } from "@/config/auth";
import { clearTokens } from "@/integrations/keycloak/client";
import { isEmbedded } from "@/embed/host";

const HOST_SESSION_KEY = "musa_embed_host_session";

/** Mesma chave do persister em App.tsx. */
const PERSISTED_CACHE_KEY = "MUSA_PORTAL_CACHE";

/**
 * Trocou de sessão?
 *
 * Só com os dois lados presentes. Sem valor guardado não há o que comparar — é
 * a primeira carga nesta aba, e apagar ali seria derrubar quem acabou de
 * entrar. Sem valor declarado é o round-trip do Keycloak, que come a query
 * string; nesse caso o guardado continua valendo.
 */
export function hostSessionChanged(declared: string | null, stored: string | null): boolean {
  if (!declared || !stored) return false;
  return declared !== stored;
}

/**
 * Roda no bootstrap, antes do React montar — ver main.tsx.
 *
 * Tarde demais e o primeiro render já teria lido o token antigo: o
 * `useKeycloakAuth` confere a autenticação na montagem, e o usuário veria a
 * tela do dono anterior da aba por um instante.
 */
export function syncHostSession(): void {
  if (typeof window === "undefined" || !isEmbedded()) return;

  const declared = new URLSearchParams(window.location.search).get("hs");
  if (!declared) return;

  try {
    if (hostSessionChanged(declared, sessionStorage.getItem(HOST_SESSION_KEY))) {
      clearTokens();
      // Cache do react-query é dado do usuário anterior: manifestos, unidades,
      // nome na tela. Limpar só o token deixaria a tela mentindo até o primeiro
      // refetch.
      localStorage.removeItem(PERSISTED_CACHE_KEY);
      // A trava anti-loop cai junto: o host tem sessão nova e o SSO responde
      // sem formulário. Sem soltar, o guest iria direto para o login.
      clearSilentAuthAttempt();
    }
    sessionStorage.setItem(HOST_SESSION_KEY, declared);
  } catch {
    // Sem storage não há como comparar nem guardar. O embed degrada para o que
    // era antes desta proteção, em vez de não carregar.
  }
}
